/**
 * Shared taxonomy for Atlas's 9 catalogued object kinds — the single source
 * of truth for icon/label/color per kind, used by every cross-kind
 * relationship view (RelationshipEgoGraph, ObjectChip, RelatedObjectsChips,
 * the global relationship map, search, and the workflow trail).
 *
 * @see docs/superpowers/specs/2026-08-10-atlas-feature-flag-relationships-design.md Part 2
 */

export type ObjectKind =
  | 'entity'
  | 'surface'
  | 'cloud-function'
  | 'event-type'
  | 'firestore-path'
  | 'feature'
  | 'flag'
  | 'hub'
  | 'statemachine';

export interface RelationEdge {
  kind: ObjectKind;
  id: string;
  /** Human-readable relation label, e.g. 'gates', 'mutates', 'triggeredBy'. */
  relation: string;
}

/**
 * Icon/label/color per kind. Colors are a fresh 9-hue set distinct from both
 * domain colors and flag-category colors (kind ≠ category ≠ domain).
 * Icons reuse each kind's existing per-view Material Symbol where one
 * already exists (surface/cloud-function/event-type/firestore-path/hub/
 * feature/flag); 'entity' and 'statemachine' get this map's only two
 * kind-level icons ('category', 'account_tree' — the latter already used
 * per-entity-shape in icons/catalog.ts's ENTITY_SHAPE_ICONS.fsm, reused here
 * at the kind level).
 */
export const KIND_META: Record<ObjectKind, { icon: string; label: string; color: string }> = {
  entity: { icon: 'category', label: 'Entity', color: '#A78BFA' },
  surface: { icon: 'web', label: 'Surface', color: '#67E8F9' },
  'cloud-function': { icon: 'bolt', label: 'Cloud Function', color: '#FCD34D' },
  'event-type': { icon: 'event', label: 'Event Type', color: '#FB7185' },
  'firestore-path': { icon: 'storage', label: 'Firestore Path', color: '#94A3B8' },
  feature: { icon: 'hexagon', label: 'Feature', color: '#34D399' },
  flag: { icon: 'toggle_on', label: 'Flag', color: '#FDBA74' },
  hub: { icon: 'dashboard', label: 'Hub', color: '#7DD3FC' },
  statemachine: { icon: 'account_tree', label: 'State Machine', color: '#F0ABFC' },
};

/**
 * Product/business kinds that bridge product thinking into the technical
 * catalog — every edge from one of these back into a PHYSICAL_KINDS member
 * is a "this business concept is implemented by that code" relationship.
 * Used by DomainMapView's L1 panel (feature hexagons, flag badges, hub
 * hexagons) and its flattened cross-domain Relationship Map entry.
 */
export const LOGICAL_KINDS: ObjectKind[] = ['feature', 'flag', 'hub'];

/**
 * The original, purely technical catalog kinds — everything LOGICAL_KINDS
 * is implemented by. Used by DomainMapView's L2 panel to group a feature or
 * hub's relationshipsFor() edges into physical sections.
 */
export const PHYSICAL_KINDS: ObjectKind[] = [
  'entity', 'surface', 'cloud-function', 'event-type', 'firestore-path', 'statemachine',
];
